import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import useRanking from 'src/hooks/api/useRanking';
import Rank from 'src/interfaces/api/Ranking';
import { LegacyUser } from 'src/interfaces/api/User';
import { LegacyEmployee } from 'src/interfaces/api/Employee';
import moment from 'moment';

interface RankProps {
  ranks: Rank[];
  userRankIndex?: number;
  userPoints: number;
}

const RankContext = createContext<RankProps>(undefined!);

const useProvideRank = (
  user?: LegacyUser & {
    typeUser: Omit<LegacyEmployee, 'user'>;
  }
): RankProps => {
  const { getAll } = useRanking();
  const [ranks, setRanks] = useState<Rank[]>([]);

  useEffect(() => {
    getAll().then((collection) => {
      setRanks(collection.items.sort((a, b) => a.points - b.points));
    });
  }, []);

  const userPoints = useMemo(() => {
    const entryDate = user?.typeUser?.entryDate || user?.typeUser?.dateCreate;
    if (entryDate) {
      return moment(moment()).diff(entryDate, 'day');
    }
    return 0;
  }, [user]);

  const currentRankIndex = useMemo((): undefined | number => {
    let userRankIndex: undefined | number;

    for (let i = 0; i < (ranks.length || 0); i++) {
      if (userPoints > ranks[i].points) {
        userRankIndex = i;
      } else {
        break;
      }
    }

    return userRankIndex;
  }, [ranks, userPoints]);

  return {
    userPoints,
    userRankIndex: currentRankIndex,
    ranks,
  };
};

const RankProvider = ({ user, children }) => {
  const values = useProvideRank(user);

  return <RankContext.Provider value={values}>{children}</RankContext.Provider>;
};

const useRank = () => useContext(RankContext);

export { RankProvider };
export default useRank;
